import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import moment from "moment-timezone";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomEvent } from "schemas/functions";
import { languageCodeToName } from "schemas/languages";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import FollowUpDelayDivider from "components/FollowUpDelayDivider";
import TemplateEditButton from "components/TemplateEditButton";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import {
  DAYS_TO_DELAY_FOLLOWUP,
  MAXIMUM_FOLLOWUP_ALLOWED,
} from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { useRegisterSaveAsNew } from "utils/useRegisterSaveAsNew";

import { SOURCE_TYPE_LABELS } from "../NewAutomation/schema";
import {
  REPITCHING_DESCRIPTION,
  hintSx,
  inputSx,
  labelSx,
  radioCardLabelSx,
  radioCardRowSx,
  radioCardSx,
  radioCircleInnerSx,
  radioCircleOuterSx,
  switchSx,
} from "../styles";
import { OutreachAutomation, OutreachAutomationType } from "../types";

type TemplateError = {
  idx: number;
  message: string;
};

/* ── Helpers ────────────────────────────────────────────────────────── */

function getBrandCountOptions(
  sourceType: OutreachAutomationType,
): { value: number; label: string }[] {
  const steps =
    sourceType === "bento_database"
      ? [5, 10, 15, 25, 50, 75, 100, 125]
      : [5, 10, 15, 25, 50];
  return steps.map((n) => ({ value: n, label: `${n}` }));
}

const TIMEZONE_OPTIONS = moment.tz.names();

interface ConfigSummaryRow {
  label: string;
  value: string;
  chips?: string[];
}

function parseGeopoints(geopoints: (string | Record<string, any>)[]): string[] {
  return geopoints.map((g) => {
    try {
      const parsed = typeof g === "string" ? JSON.parse(g) : g;
      return parsed.label || parsed.name || "Location";
    } catch {
      return "Location";
    }
  });
}

function formatFollowerRange(
  min?: number | null,
  max?: number | null,
): string | null {
  const fmt = (n: number) => {
    if (n >= 1_000_000) return `${n / 1_000_000}M`;
    if (n >= 1_000) return `${n / 1_000}K`;
    return `${n}`;
  };
  if (min != null && min > 0 && max != null && max > 0) {
    return `${fmt(min)} – ${fmt(max)} followers`;
  }
  if (min != null && min > 0) return `${fmt(min)}+ followers`;
  if (max != null && max > 0) return `Up to ${fmt(max)} followers`;
  return null;
}

function getConfigSummaryRows(
  sourceType: OutreachAutomationType,
  config: Record<string, any> | null | undefined,
  brandCount: number,
): ConfigSummaryRow[] {
  const rows: ConfigSummaryRow[] = [];
  rows.push({ label: "Source:", value: SOURCE_TYPE_LABELS[sourceType] });

  if (config) {
    if (sourceType === "google_places") {
      if (config.search_queries?.length) {
        rows.push({
          label: "Search:",
          value: config.search_queries.join(", "),
        });
      }
      if (config.geopoints?.length) {
        rows.push({
          label: "Locations:",
          value: "",
          chips: parseGeopoints(config.geopoints),
        });
      }
    } else if (sourceType === "instagram_tracked_creators") {
      if (config.instagram_usernames?.length) {
        rows.push({
          label: "Tracking:",
          value: "",
          chips: config.instagram_usernames.map((u: string) => `@${u}`),
        });
      }
      if (config.geopoints?.length) {
        rows.push({
          label: "Countries:",
          value: "",
          chips: parseGeopoints(config.geopoints),
        });
      }
      if (config.category_ids?.length) {
        rows.push({
          label: "Categories:",
          value: `${config.category_ids.length} selected`,
        });
      }
      const igFollowers = formatFollowerRange(
        config.min_instagram_followers,
        config.max_instagram_followers,
      );
      if (igFollowers) {
        rows.push({ label: "Followers:", value: igFollowers });
      }
      if (config.languages?.length) {
        rows.push({
          label: "Languages:",
          value: "",
          chips: config.languages.map(
            (l: string) => languageCodeToName(l) || l,
          ),
        });
      }
    } else if (sourceType === "bento_database") {
      const sp = config.search_parameters;
      if (sp?.query) {
        rows.push({ label: "Search:", value: sp.query });
      }
      if (sp?.category_ids?.length) {
        rows.push({
          label: "Categories:",
          value: `${sp.category_ids.length} selected`,
        });
      }
      if (sp?.geopoints?.length) {
        rows.push({
          label: "Locations:",
          value: "",
          chips: parseGeopoints(sp.geopoints),
        });
      }
      const bentoFollowers = formatFollowerRange(
        sp?.min_instagram_followers,
        sp?.max_instagram_followers,
      );
      if (bentoFollowers) {
        rows.push({ label: "Followers:", value: bentoFollowers });
      }
      if (sp?.languages?.length) {
        rows.push({
          label: "Languages:",
          value: "",
          chips: sp.languages.map((l: string) => languageCodeToName(l) || l),
        });
      }
      if (sp?.target_genders?.length) {
        rows.push({
          label: "Gender:",
          value: sp.target_genders
            .map((g: string) => g.charAt(0).toUpperCase() + g.slice(1))
            .join(", "),
        });
      }
    }
  }

  rows.push({ label: "Frequency:", value: "Weekly" });
  rows.push({
    label: "Expected:",
    value: `~${brandCount} new brands per week`,
  });

  return rows;
}

/* ── Shared sx tokens ─────────────────────────────────────────────── */

const sectionSx = {
  backgroundColor: "white",
  border: "1px solid rgba(1,67,57,0.15)",
  borderRadius: "14px",
  p: 2.5,
  mb: 2.5,
};

/* ── Component ────────────────────────────────────────────────────── */

interface EditAutomationDrawerProps {
  automation: OutreachAutomation | null;
  onClose: () => void;
  onSaved: (updated: OutreachAutomation, brandsChanged?: boolean) => void;
  onDelete: (id: number) => void;
}

export default function EditAutomationDrawer({
  automation,
  onClose,
  onSaved,
  onDelete,
}: EditAutomationDrawerProps) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    fetchOutreachTemplates,
    setCreateTemplateDialogOpen,
    setCurrentTemplateContentType,
  } = useContext(OutreachTemplatesContext);
  const { emailSettings } = useContext(OrganizationUserContext);

  // Full automation data (includes templateIds, timezone from GET)
  const [fullAutomation, setFullAutomation] =
    useState<OutreachAutomation | null>(null);
  const [loadingData, setLoadingData] = useState(false);

  // Settings form state
  const [name, setName] = useState("");
  const [brandCount, setBrandCount] = useState(10);
  const [approvalMode, setApprovalMode] = useState<
    "auto_schedule" | "approval_required"
  >("approval_required");
  const [timezone, setTimezone] = useState(moment.tz.guess());

  // Template state
  const [modifiedTemplates, setModifiedTemplates] = useState<Template[]>([]);
  const templatesInitializedRef = useRef(false);
  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [templateWarningOpen, setTemplateWarningOpen] = useState(false);
  const [updateExistingBrands, setUpdateExistingBrands] = useState(false);
  const templatesErrorRef = useRef<HTMLDivElement>(null);

  const [saving, setSaving] = useState(false);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [pauseOriginalOnDuplicate, setPauseOriginalOnDuplicate] =
    useState(true);
  const [approvalModeDialogOpen, setApprovalModeDialogOpen] = useState(false);
  const [convertExistingBrands, setConvertExistingBrands] = useState(false);
  const [repitchingEnabled, setRepitchingEnabled] = useState(false);

  const navigate = useNavigate();

  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template: { templateId: number }) => template.templateId,
  );
  const selectedFollowups = modifiedTemplates?.slice(1);

  useRegisterSaveAsNew(
    (newTemplate, editedId) =>
      setModifiedTemplates((prev) =>
        prev.map((t) =>
          t.id === editedId ? { ...newTemplate, delayDays: t.delayDays } : t,
        ),
      ),
    Boolean(automation?.id),
  );

  // Fetch full automation data (includes templateIds, timezone)
  const fetchFullAutomation = async () => {
    if (!currentOrg?.id || !automation?.id) return;
    setLoadingData(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${automation.id}`,
      );
      setFullAutomation(res.outreachAutomation);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (automation) {
      trackEvent("Autopilot Edit Automation Drawer Opened", {
        "Automation ID": automation.id,
        "Automation Type": automation.type,
        Status: automation.status,
      });
      fetchFullAutomation();
      if (!allTemplates?.length) {
        fetchOutreachTemplates();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automation?.id]);

  // Reset initialization flag when automation changes
  useEffect(() => {
    templatesInitializedRef.current = false;
  }, [automation?.id]);

  // Initialize form when fullAutomation loads
  useEffect(() => {
    if (!fullAutomation) return;
    setName(fullAutomation.name || "");
    setBrandCount(fullAutomation.brandCount);
    setApprovalMode(fullAutomation.approvalMode);
    setTimezone(fullAutomation.timezone || moment.tz.guess());
    setRepitchingEnabled(fullAutomation.repitchingEnabled ?? false);

    // Initialize templates from templateIds (only once per automation)
    if (
      !templatesInitializedRef.current &&
      fullAutomation.templateIds?.length &&
      allTemplates?.length
    ) {
      const matched: Template[] = [];
      fullAutomation.templateIds.forEach((tid, idx) => {
        const template = allTemplates.find((t: Template) => t.id === tid);
        if (template) {
          const delayDays =
            idx === 0
              ? undefined
              : fullAutomation.templateDelayDays?.[idx] ??
                DAYS_TO_DELAY_FOLLOWUP;
          matched.push({ ...template, delayDays });
        }
      });
      if (matched.length > 0) {
        setModifiedTemplates(matched);
        templatesInitializedRef.current = true;
      }
    }
  }, [fullAutomation, allTemplates]);

  const outreachTemplate = useMemo(
    () =>
      modifiedTemplates.find(
        (t) => t.contentType === TemplateContentType.OUTREACH,
      ),
    [modifiedTemplates],
  );

  const brandCountOptions = automation
    ? getBrandCountOptions(automation.type)
    : [];

  const configRows = automation
    ? getConfigSummaryRows(
        automation.type,
        automation.configuration,
        brandCount,
      )
    : [];

  useEffect(() => {
    if (templatesError) {
      templatesErrorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [templatesError]);

  // Check if templates changed from original
  const templatesChanged = useMemo(() => {
    if (!fullAutomation?.templateIds) return false;
    const currentIds = modifiedTemplates
      .map((t) => t.id)
      .filter((id): id is number => id !== undefined);
    if (currentIds.length !== fullAutomation.templateIds.length) return true;
    if (currentIds.some((id, i) => id !== fullAutomation.templateIds![i]))
      return true;
    const originalDelays = fullAutomation.templateDelayDays ?? [];
    return modifiedTemplates.some((t, idx) => {
      if (idx === 0) return false;
      const original = originalDelays[idx] ?? DAYS_TO_DELAY_FOLLOWUP;
      const current = t.delayDays ?? DAYS_TO_DELAY_FOLLOWUP;
      return original !== current;
    });
  }, [
    modifiedTemplates,
    fullAutomation?.templateIds,
    fullAutomation?.templateDelayDays,
  ]);

  /* ── Handlers ── */

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...modifiedTemplates];
    const template = allTemplates?.find((x: Template) => x.id === value);
    if (template) {
      copy[index] = { ...template, delayDays: copy[index]?.delayDays };
      setModifiedTemplates(copy);
      setTemplatesError(null);
    }
  };

  const handleDelayChange = (index: number, value: number) => {
    const copy = [...modifiedTemplates];
    if (copy[index]) {
      copy[index] = { ...copy[index], delayDays: value };
      setModifiedTemplates(copy);
    }
  };

  const addFollowUp = () => {
    if (filteredFollowUps?.[0]) {
      setModifiedTemplates([
        ...modifiedTemplates,
        { ...filteredFollowUps[0], delayDays: DAYS_TO_DELAY_FOLLOWUP },
      ]);
    } else {
      setCreateTemplateDialogOpen(true);
      setCurrentTemplateContentType(TemplateContentType.FOLLOWUP);
    }
  };

  const removeFollowUp = (index: number) => {
    const copy = [...modifiedTemplates];
    copy.splice(index, 1);
    setModifiedTemplates(copy);
  };

  const approvalModeChanged =
    fullAutomation != null && approvalMode !== fullAutomation.approvalMode;

  const doSave = async (shouldConvertBrands: boolean) => {
    if (!currentOrg?.id || !automation?.id) return;

    setSaving(true);
    setTemplatesError(null);
    try {
      const templateIds = modifiedTemplates
        .map((t) => t.id)
        .filter((id): id is number => id !== undefined);

      const body: Record<string, any> = {
        name: name.trim(),
        brandCount,
        approvalMode,
        timezone,
        repitchingEnabled,
      };

      if (approvalModeChanged) {
        body.convertExistingBrands = shouldConvertBrands;
      }

      // Only send templateIds if they changed
      if (templatesChanged) {
        body.templateIds = templateIds;
        body.templateDelayDays = modifiedTemplates.map((t, idx) =>
          idx === 0 ? null : t.delayDays ?? null,
        );
        body.updateExistingBrands = updateExistingBrands;
      }

      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${automation.id}/settings`,
        "PUT",
        {},
        body,
        true, // returnResult to inspect template errors
      );
      const json = await res.json();
      if (!res.ok) {
        if (json.templateIdx !== undefined) {
          setTemplatesError({ idx: json.templateIdx, message: json.message });
        } else {
          throw new Error(json.message);
        }
        return;
      }
      setAlert("Automation updated successfully", "success");
      onSaved(
        json.outreachAutomation,
        approvalModeChanged && shouldConvertBrands,
      );
      handleClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaving(false);
      setTemplateWarningOpen(false);
      setApprovalModeDialogOpen(false);
    }
  };

  const handleSave = async () => {
    if (!currentOrg?.id || !automation?.id) return;

    // If templates changed, show warning first
    if (templatesChanged && !templateWarningOpen) {
      setTemplateWarningOpen(true);
      return;
    }

    // If approval mode changed, ask about existing brands
    if (approvalModeChanged && !approvalModeDialogOpen) {
      setApprovalModeDialogOpen(true);
      return;
    }

    await doSave(convertExistingBrands);
  };

  const handleClose = () => {
    setFullAutomation(null);
    setModifiedTemplates([]);
    setTemplatesError(null);
    setTemplateWarningOpen(false);
    setPauseDialogOpen(false);
    setDeleteDialogOpen(false);
    setDuplicateDialogOpen(false);
    setPauseOriginalOnDuplicate(true);
    setApprovalModeDialogOpen(false);
    setConvertExistingBrands(false);
    setUpdateExistingBrands(false);
    onClose();
  };

  const handleDeleteConfirm = () => {
    if (automation) {
      onDelete(automation.id);
    }
    setDeleteDialogOpen(false);
    handleClose();
  };

  const handlePauseConfirm = async () => {
    if (!currentOrg?.id || !automation?.id) return;
    setPausing(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${automation.id}/pause`,
        "POST",
      );
      setAlert("Automation paused", "success");
      onSaved(res.outreachAutomation);
      handleClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setPausing(false);
      setPauseDialogOpen(false);
    }
  };

  const handleResume = async () => {
    if (!currentOrg?.id || !automation?.id) return;
    setPausing(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${automation.id}/resume`,
        "POST",
      );
      setAlert("Automation resumed", "success");
      onSaved(res.outreachAutomation);
      handleClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setPausing(false);
    }
  };

  const handleDuplicateConfirm = async () => {
    if (!currentOrg?.id || !automation?.id) return;
    setDuplicating(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${automation.id}/duplicate`,
        "POST",
        {},
        { pauseOriginal: pauseOriginalOnDuplicate },
      );
      if (
        pauseOriginalOnDuplicate &&
        (automation.status === "live" || automation.status === "pending")
      ) {
        onSaved({ ...automation, status: "paused" });
      }
      setAlert("Automation duplicated as a new draft", "success");
      handleClose();
      navigate(
        `/dashboard/autopilot/new/${res.outreachAutomation.id}/configure`,
      );
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setDuplicating(false);
      setDuplicateDialogOpen(false);
    }
  };

  if (!automation) return null;

  return (
    <>
      <Drawer open={!!automation} anchor="right" onClose={handleClose}>
        <Box
          sx={{
            width: { xs: "100vw", sm: 480 },
            maxWidth: "100vw",
            p: 3,
            overflowY: "auto",
            height: "100%",
            backgroundColor: "#fafaf6",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 3,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 20,
                  color: "primary.main",
                  lineHeight: "28px",
                }}
              >
                Edit Automation
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: "rgba(1,67,57,0.6)",
                  lineHeight: "20px",
                }}
              >
                {automation.name || SOURCE_TYPE_LABELS[automation.type]}
              </Typography>
            </Box>
            <IconButton onClick={handleClose} sx={{ mt: -0.5 }}>
              <Box
                component="span"
                className="fa-regular fa-xmark"
                sx={{ fontSize: 16, color: "rgba(1,67,57,0.5)" }}
              />
            </IconButton>
          </Box>

          {loadingData ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Skeleton
                variant="rounded"
                height={120}
                sx={{ borderRadius: "14px" }}
              />
              <Skeleton
                variant="rounded"
                height={200}
                sx={{ borderRadius: "14px" }}
              />
              <Skeleton
                variant="rounded"
                height={160}
                sx={{ borderRadius: "14px" }}
              />
            </Box>
          ) : (
            <>
              {/* ── Section 1: Configuration (read-only) ── */}
              <Box sx={sectionSx}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box
                    component="i"
                    className="fa-solid fa-chart-line"
                    sx={{ fontSize: 14, color: "primary.main" }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 16,
                      color: "primary.main",
                    }}
                  >
                    Configuration
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "rgba(1,67,57,0.4)",
                      fontWeight: 500,
                      ml: 0.5,
                    }}
                  >
                    read-only
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {configRows.map((row, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 13,
                          color: "rgba(1,67,57,0.6)",
                          lineHeight: "20px",
                          width: 90,
                          flexShrink: 0,
                        }}
                      >
                        {row.label}
                      </Typography>
                      {row.chips ? (
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {row.chips.map((chip, ci) => (
                            <Chip
                              key={ci}
                              label={chip}
                              size="small"
                              sx={{
                                backgroundColor: "rgba(1,67,57,0.08)",
                                color: "primary.main",
                                fontWeight: 500,
                                fontSize: 12,
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "primary.main",
                            lineHeight: "20px",
                            flex: 1,
                          }}
                        >
                          {row.value}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>

                {automation.status !== "draft" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      backgroundColor: "rgba(1,67,57,0.03)",
                      border: "1px solid rgba(1,67,57,0.08)",
                      borderRadius: "10px",
                      px: 2,
                      py: 1.5,
                      mt: 2,
                    }}
                  >
                    <Box
                      component="i"
                      className="fa-regular fa-circle-info"
                      sx={{
                        fontSize: 13,
                        color: "rgba(1,67,57,0.4)",
                        mt: "2px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "rgba(1,67,57,0.5)",
                        lineHeight: "18px",
                      }}
                    >
                      The configuration can't be changed after activation.{" "}
                      <Box
                        component="span"
                        onClick={() => setDuplicateDialogOpen(true)}
                        sx={{
                          color: "primary.main",
                          fontWeight: 600,
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        Duplicate
                      </Box>{" "}
                      to use different settings.
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* ── Section 2: Settings (editable) ── */}
              <Box sx={sectionSx}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box
                    component="i"
                    className="fa-solid fa-gear"
                    sx={{ fontSize: 14, color: "primary.main" }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 16,
                      color: "primary.main",
                    }}
                  >
                    Settings
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                  }}
                >
                  {/* Name */}
                  <Box>
                    <Typography sx={{ ...labelSx, mb: "6px" }}>
                      Automation Name
                    </Typography>
                    <TextField
                      fullWidth
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter a name for this automation"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          "& fieldset": {
                            borderColor: "rgba(1,67,57,0.2)",
                            borderWidth: 2,
                          },
                          "&:hover fieldset": { borderColor: "primary.main" },
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* Pitches per week */}
                  <Box>
                    <Typography sx={{ ...labelSx, mb: "6px" }}>
                      Target Pitches Per Week
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                      }}
                    >
                      <Select
                        value={brandCount}
                        onChange={(e) => setBrandCount(Number(e.target.value))}
                        size="small"
                        sx={{ ...inputSx, flex: 1 }}
                      >
                        {brandCountOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "rgba(1,67,57,0.6)",
                          flexShrink: 0,
                        }}
                      >
                        pitches/week
                      </Typography>
                    </Box>
                  </Box>

                  {/* Sending mode */}
                  <Box>
                    <Typography sx={{ ...labelSx, mb: "6px" }}>
                      Sending Mode
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {(
                        [
                          {
                            value: "auto_schedule" as const,
                            label: "Auto-send",
                          },
                          {
                            value: "approval_required" as const,
                            label: "Review first",
                          },
                        ] as const
                      ).map((opt) => (
                        <Box
                          key={opt.value}
                          onClick={() => setApprovalMode(opt.value)}
                          sx={{
                            flex: 1,
                            border: "2px solid",
                            borderColor:
                              approvalMode === opt.value
                                ? "primary.main"
                                : "rgba(1,67,57,0.15)",
                            borderRadius: "10px",
                            px: 1.5,
                            py: 1,
                            cursor: "pointer",
                            backgroundColor:
                              approvalMode === opt.value
                                ? "rgba(1,67,57,0.03)"
                                : "white",
                            transition: "all 0.15s",
                            "&:hover": { borderColor: "primary.main" },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                border: "2px solid",
                                borderColor:
                                  approvalMode === opt.value
                                    ? "primary.main"
                                    : "rgba(1,67,57,0.3)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {approvalMode === opt.value && (
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: "primary.main",
                                  }}
                                />
                              )}
                            </Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: 13,
                                color: "primary.main",
                              }}
                            >
                              {opt.label}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Timezone */}
                  <Box>
                    <Typography sx={{ ...labelSx, mb: "6px" }}>
                      Timezone
                    </Typography>
                    <Autocomplete
                      options={TIMEZONE_OPTIONS}
                      value={timezone}
                      onChange={(_e, newValue) => {
                        if (newValue) setTimezone(newValue);
                      }}
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select timezone"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              "& fieldset": {
                                borderColor: "rgba(1,67,57,0.2)",
                                borderWidth: 2,
                              },
                              "&:hover fieldset": {
                                borderColor: "primary.main",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>

                  {/* Sending Schedule Info */}
                  <Box
                    sx={{
                      backgroundColor: "rgba(1,67,57,0.03)",
                      border: "1px solid rgba(1,67,57,0.1)",
                      borderRadius: "10px",
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Box
                        component="i"
                        className="fa-regular fa-clock"
                        sx={{ fontSize: 14, color: "rgba(1,67,57,0.6)" }}
                      />
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 13,
                          color: "primary.main",
                        }}
                      >
                        Sending Schedule
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      <Box
                        component="li"
                        sx={{ color: "rgba(1,67,57,0.5)", mb: 0.5 }}
                      >
                        <Typography sx={hintSx}>
                          Emails are only sent during business hours (9 AM – 4
                          PM)
                        </Typography>
                      </Box>
                      <Box
                        component="li"
                        sx={{ color: "rgba(1,67,57,0.5)", mb: 0.5 }}
                      >
                        <Typography sx={hintSx}>
                          No emails are sent on weekends
                        </Typography>
                      </Box>
                      <Box component="li" sx={{ color: "rgba(1,67,57,0.5)" }}>
                        <Typography sx={hintSx}>
                          Times are based on the timezone selected above
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 2,
                      backgroundColor: "rgba(177,179,248,0.1)",
                      border: "1px solid rgba(177,179,248,0.3)",
                      borderRadius: "10px",
                      px: "18px",
                      py: "14px",
                    }}
                  >
                    <Box>
                      <Typography sx={{ ...labelSx, mb: "4px" }}>
                        Enable Repitching
                      </Typography>
                      <Typography sx={hintSx}>
                        {REPITCHING_DESCRIPTION}
                      </Typography>
                    </Box>
                    <Switch
                      checked={repitchingEnabled}
                      onChange={(e) => setRepitchingEnabled(e.target.checked)}
                      sx={{ flexShrink: 0, ...switchSx }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* ── Section 3: Email Templates (editable) ── */}
              <Box sx={sectionSx} ref={templatesErrorRef}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box
                    component="i"
                    className="fa-regular fa-envelope"
                    sx={{ fontSize: 14, color: "primary.main" }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 16,
                      color: "primary.main",
                    }}
                  >
                    Email Templates
                  </Typography>
                </Box>

                {!allTemplates?.length ||
                (fullAutomation?.templateIds?.length &&
                  modifiedTemplates.length === 0) ? (
                  <Skeleton
                    variant="rounded"
                    height={80}
                    sx={{ borderRadius: "10px" }}
                  />
                ) : (
                  <Box>
                    {/* Outreach template */}
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 13,
                            color: "primary.main",
                          }}
                        >
                          Outreach Template
                          <Typography
                            component="span"
                            sx={{ color: "error.main", ml: 0.5, fontSize: 13 }}
                          >
                            *
                          </Typography>
                        </Typography>
                      </Box>
                      {outreachTemplate ||
                      filteredOutreachTemplates?.length > 0 ? (
                        <OutreachSelect
                          templates={modifiedTemplates}
                          handleChange={handleTemplateChange}
                          filteredOutreachTemplates={filteredOutreachTemplates}
                          templateFromEmailSettings={templateFromEmailSettings}
                          addNewOption={() => {
                            setCreateTemplateDialogOpen(true);
                            setCurrentTemplateContentType(
                              TemplateContentType.OUTREACH,
                            );
                          }}
                          templatesError={templatesError}
                        />
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            setCreateTemplateDialogOpen(true);
                            setCurrentTemplateContentType(
                              TemplateContentType.OUTREACH,
                            );
                          }}
                          disableElevation
                          sx={{
                            height: 32,
                            textTransform: "none",
                            backgroundColor: "primary.main",
                            "&:hover": { backgroundColor: "#01332b" },
                          }}
                        >
                          Generate Template
                        </Button>
                      )}
                    </Box>

                    {/* Divider */}
                    <Box
                      sx={{
                        height: "1px",
                        backgroundColor: "rgba(1,67,57,0.1)",
                        my: 2,
                      }}
                    />

                    {/* Follow-ups */}
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 13,
                            color: "primary.main",
                          }}
                        >
                          Follow-up Templates
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "rgba(1,67,57,0.4)",
                            fontWeight: 500,
                          }}
                        >
                          optional
                        </Typography>
                      </Box>

                      {selectedFollowups?.map((followup, index) => (
                        <Box key={index}>
                          <FollowUpDelayDivider
                            days={followup?.delayDays ?? DAYS_TO_DELAY_FOLLOWUP}
                            onChange={(val) =>
                              handleDelayChange(index + 1, val)
                            }
                            isFirst={index === 0}
                            align="start"
                          />
                          <Grid container alignItems="center" gap={1}>
                            <Grid item xs>
                              <Select
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleTemplateChange(e, index + 1);
                                }}
                                value={followup?.id}
                                size="small"
                                fullWidth
                                sx={inputSx}
                              >
                                {filteredFollowUps?.map((template) => (
                                  <MenuItem
                                    key={template.id}
                                    value={template.id}
                                  >
                                    <Typography noWrap sx={{ maxWidth: 250 }}>
                                      {template.name}
                                    </Typography>
                                  </MenuItem>
                                ))}
                              </Select>
                            </Grid>
                            {followup?.id && (
                              <Grid item xs="auto">
                                <TemplateEditButton templateId={followup.id} />
                              </Grid>
                            )}
                            <Grid item xs="auto">
                              <IconButton
                                sx={{
                                  fontSize: 12,
                                  color: "rgba(1,67,57,0.4)",
                                }}
                                onClick={() => removeFollowUp(index + 1)}
                              >
                                <Box
                                  component="i"
                                  className="fa-regular fa-trash"
                                />
                              </IconButton>
                            </Grid>
                          </Grid>

                          {templatesError &&
                            templatesError.idx === index + 1 && (
                              <Typography
                                sx={{
                                  mt: 0.5,
                                  color: "warning.main",
                                  fontSize: 13,
                                }}
                              >
                                <Box
                                  component="i"
                                  className="fa-regular fa-warning"
                                  sx={{ mr: 0.5 }}
                                />
                                {templatesError.message}
                              </Typography>
                            )}
                        </Box>
                      ))}

                      {modifiedTemplates.length <
                        MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
                        <Button
                          onClick={addFollowUp}
                          variant="outlined"
                          size="small"
                          sx={{
                            mt: 2,
                            textTransform: "none",
                            borderColor: "rgba(1,67,57,0.15)",
                            color: "primary.main",
                            borderRadius: "10px",
                            fontSize: 12,
                            "&:hover": {
                              borderColor: "primary.main",
                              backgroundColor: "rgba(1,67,57,0.04)",
                            },
                          }}
                        >
                          + Add Follow-up
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* ── Action buttons ── */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  mt: 1,
                }}
              >
                <LoadingButton
                  variant="contained"
                  fullWidth
                  loading={saving}
                  onClick={handleSave}
                  disabled={!name.trim()}
                  sx={{
                    borderRadius: "10px",
                    backgroundColor: "primary.main",
                    fontWeight: 600,
                    fontSize: 14,
                    py: 1.25,
                    textTransform: "none",
                  }}
                >
                  Save Changes
                </LoadingButton>

                {automation.status !== "draft" && (
                  <>
                    {automation.status === "live" ||
                    automation.status === "pending" ? (
                      <LoadingButton
                        variant="outlined"
                        fullWidth
                        loading={pausing}
                        onClick={() => setPauseDialogOpen(true)}
                        sx={{
                          borderRadius: "10px",
                          backgroundColor: "#fff7ed",
                          borderColor: "#ffd6a8",
                          color: "#ca3500",
                          fontWeight: 600,
                          fontSize: 14,
                          py: 1.25,
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#fff0de",
                            borderColor: "#ffbf80",
                          },
                        }}
                      >
                        <Box
                          component="span"
                          className="fa-solid fa-pause"
                          sx={{ fontSize: 12, mr: 1 }}
                        />
                        Pause Automation
                      </LoadingButton>
                    ) : (
                      <LoadingButton
                        variant="contained"
                        fullWidth
                        loading={pausing}
                        onClick={handleResume}
                        sx={{
                          borderRadius: "10px",
                          backgroundColor: "#014339",
                          color: "white",
                          fontWeight: 600,
                          fontSize: 14,
                          py: 1.25,
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#012e27",
                          },
                        }}
                      >
                        <Box
                          component="span"
                          className="fa-solid fa-play"
                          sx={{ fontSize: 12, mr: 1 }}
                        />
                        Resume Automation
                      </LoadingButton>
                    )}
                  </>
                )}

                {automation.status !== "draft" && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setDuplicateDialogOpen(true)}
                    sx={{
                      borderRadius: "10px",
                      backgroundColor: "#fafaf6",
                      borderColor: "rgba(1,67,57,0.1)",
                      color: "primary.main",
                      fontWeight: 600,
                      fontSize: 14,
                      py: 1.25,
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "primary.main",
                        backgroundColor: "rgba(1,67,57,0.04)",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      className="fa-regular fa-copy"
                      sx={{ fontSize: 12, mr: 1 }}
                    />
                    Duplicate Automation
                  </Button>
                )}

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{
                    borderRadius: "10px",
                    borderColor: "rgba(1,67,57,0.15)",
                    color: "rgba(1,67,57,0.6)",
                    fontWeight: 600,
                    fontSize: 14,
                    py: 1.25,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "error.main",
                      color: "error.main",
                      backgroundColor: "rgba(239,68,68,0.04)",
                    },
                  }}
                >
                  Delete Automation
                </Button>
              </Box>

              {/* Disclaimer */}
              <Typography
                sx={{
                  fontSize: 12,
                  color: "rgba(1,67,57,0.4)",
                  mt: 2,
                  textAlign: "center",
                }}
              >
                Changes may take up to 7 days to fully take effect for ongoing
                campaigns.
              </Typography>
            </>
          )}
        </Box>
      </Drawer>

      {/* Template change dialog */}
      <ConfirmDialog
        header="Update Templates?"
        subheader={
          <Box>
            <Typography sx={{ mb: 2 }}>
              Would you like to also update existing drafts and scheduled emails
              with the new templates?
              <br />
              <br /> Pending outreach emails will be rescheduled and emails that
              have already sent and their associated follow-ups will not be
              changed.
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {(
                [
                  {
                    value: true,
                    label: "Yes, update drafts and emails",
                  },
                  { value: false, label: "No, only apply to future brands" },
                ] as const
              ).map((opt) => (
                <Box
                  key={String(opt.value)}
                  onClick={() => setUpdateExistingBrands(opt.value)}
                  sx={radioCardSx(updateExistingBrands === opt.value)}
                >
                  <Box sx={radioCardRowSx}>
                    <Box
                      sx={radioCircleOuterSx(
                        updateExistingBrands === opt.value,
                      )}
                    >
                      {updateExistingBrands === opt.value && (
                        <Box sx={radioCircleInnerSx} />
                      )}
                    </Box>
                    <Typography sx={radioCardLabelSx}>{opt.label}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        }
        open={templateWarningOpen}
        handleConfirm={handleSave}
        handleCancel={() => setTemplateWarningOpen(false)}
        handleClose={() => setTemplateWarningOpen(false)}
        confirmText="Update Templates"
        cancelText="Cancel"
        loading={saving}
      />

      {/* Pause confirmation dialog */}
      <ConfirmDialog
        header="Pause Automation?"
        subheader={
          <Typography>
            Pausing will stop this automation from discovering and pitching new
            brands. Any emails already scheduled will still be delivered as
            planned. You can resume at any time.
          </Typography>
        }
        open={pauseDialogOpen}
        handleConfirm={handlePauseConfirm}
        handleCancel={() => setPauseDialogOpen(false)}
        handleClose={() => setPauseDialogOpen(false)}
        confirmText="Pause"
        cancelText="Cancel"
        loading={pausing}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        header="Delete Automation?"
        subheader={
          <Typography>
            This will permanently stop this automation. Any emails already
            scheduled will still be delivered as planned.
          </Typography>
        }
        open={deleteDialogOpen}
        handleConfirm={handleDeleteConfirm}
        handleCancel={() => setDeleteDialogOpen(false)}
        handleClose={() => setDeleteDialogOpen(false)}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Duplicate confirmation dialog */}
      <ConfirmDialog
        header="Duplicate Automation"
        subheader={
          <Box>
            <Typography sx={{ mb: 2 }}>
              This will create a new draft copy of this automation with the same
              configuration and templates. You can review and modify it before
              activating.
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 13,
                color: "primary.main",
                mb: 1,
              }}
            >
              What should happen to the current automation?
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {(
                [
                  {
                    value: true,
                    label: "Pause the current automation",
                  },
                  {
                    value: false,
                    label: "Keep it running",
                  },
                ] as const
              ).map((opt) => (
                <Box
                  key={String(opt.value)}
                  onClick={() => setPauseOriginalOnDuplicate(opt.value)}
                  sx={radioCardSx(pauseOriginalOnDuplicate === opt.value)}
                >
                  <Box sx={radioCardRowSx}>
                    <Box
                      sx={radioCircleOuterSx(
                        pauseOriginalOnDuplicate === opt.value,
                      )}
                    >
                      {pauseOriginalOnDuplicate === opt.value && (
                        <Box sx={radioCircleInnerSx} />
                      )}
                    </Box>
                    <Typography sx={radioCardLabelSx}>{opt.label}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        }
        open={duplicateDialogOpen}
        handleConfirm={handleDuplicateConfirm}
        handleCancel={() => setDuplicateDialogOpen(false)}
        handleClose={() => setDuplicateDialogOpen(false)}
        confirmText="Duplicate"
        cancelText="Cancel"
        loading={duplicating}
      />

      {/* Approval mode change dialog */}
      <ConfirmDialog
        header="Update Sending Mode?"
        subheader={
          <Box>
            <Typography sx={{ mb: 2 }}>
              {approvalMode === "auto_schedule"
                ? "You're switching to auto-send. Would you like to automatically schedule the brands that are currently waiting for your review?"
                : "You're switching to review first. Would you like to unschedule brands that haven't been sent yet and move them back to your review queue?"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {(
                [
                  { value: true, label: "Yes, update existing brands" },
                  { value: false, label: "No, only apply to new brands" },
                ] as const
              ).map((opt) => (
                <Box
                  key={String(opt.value)}
                  onClick={() => setConvertExistingBrands(opt.value)}
                  sx={radioCardSx(convertExistingBrands === opt.value)}
                >
                  <Box sx={radioCardRowSx}>
                    <Box
                      sx={radioCircleOuterSx(
                        convertExistingBrands === opt.value,
                      )}
                    >
                      {convertExistingBrands === opt.value && (
                        <Box sx={radioCircleInnerSx} />
                      )}
                    </Box>
                    <Typography sx={radioCardLabelSx}>{opt.label}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        }
        open={approvalModeDialogOpen}
        handleConfirm={() => doSave(convertExistingBrands)}
        handleCancel={() => setApprovalModeDialogOpen(false)}
        handleClose={() => setApprovalModeDialogOpen(false)}
        confirmText="Save Changes"
        cancelText="Cancel"
        loading={saving}
      />
    </>
  );
}
